import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, ShieldCheck, Building2, GraduationCap, Lock, Eye, KeyRound, FileCheck2, Sparkles, ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fileUrl } from "@/components/Certificate";
import { SkeletonCardGrid, SkeletonInlineStats } from "@/components/PageSkeleton";
import { toast } from "sonner";

const CATEGORY_ORDER = ["High School", "University", "Professional School"];
const CATEGORY_LABEL = {
  "High School": "High Schools",
  "University": "Universities",
  "Professional School": "Professional Schools",
};

export default function PublicLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [certId, setCertId] = useState("");
  const [voucher, setVoucher] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [stats, setStats] = useState({ total_institutions: 0, total_graduates: 0 });
  const [institutions, setInstitutions] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [instSearch, setInstSearch] = useState("");

  // Group institutions by category, alphabetically sorted, active + matching search only
  const grouped = useMemo(() => {
    const q = instSearch.trim().toLowerCase();
    const out = {};
    CATEGORY_ORDER.forEach((c) => { out[c] = []; });
    institutions
      .filter((i) => i.active !== false)
      .filter((i) => !q || (i.name || "").toLowerCase().includes(q))
      .forEach((inst) => {
        const cat = CATEGORY_ORDER.includes(inst.category) ? inst.category : "University";
        out[cat].push(inst);
      });
    Object.keys(out).forEach((c) => {
      out[c].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    });
    return out;
  }, [institutions, instSearch]);

  const totalMatches = useMemo(
    () => CATEGORY_ORDER.reduce((n, c) => n + (grouped[c]?.length || 0), 0),
    [grouped]
  );

  // Auto-fill from QR-scan URL: /?cert=CS-XXX
  useEffect(() => {
    const c = searchParams.get("cert");
    if (c) setCertId(c.toUpperCase());
    const v = searchParams.get("voucher");
    if (v) setVoucher(v.toUpperCase());
  }, [searchParams]);

  useEffect(() => {
    Promise.allSettled([
      api.get("/stats").then(({ data }) => setStats(data)),
      api.get("/institutions").then(({ data }) => setInstitutions(data)),
    ]).finally(() => setInitialLoading(false));
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!certId.trim() || !voucher.trim()) {
      toast.error("Please enter both Certificate ID and Voucher Code");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/validate", {
        certificate_id: certId.trim().toUpperCase(),
        voucher_code: voucher.trim().toUpperCase(),
      });
      sessionStorage.setItem("certiseal_result", JSON.stringify(data));
      navigate("/result");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Validation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <PublicHeader />

      {/* Hero with validator */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 dark:bg-blue-500/10 blur-3xl rounded-full -translate-y-1/3 translate-x-1/3 pointer-events-none" />
        <div className="relative w-full max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm mb-8"
              >
                <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-700 dark:text-slate-300">Your Trusted Academic Proof.</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
                className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900 dark:text-white"
                data-testid="hero-title"
              >
                Verify academic credentials in <span className="text-blue-600 dark:text-blue-400">seconds</span>, not weeks.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-6 text-lg leading-relaxed text-slate-600 dark:text-slate-300 max-w-xl"
              >
                CertiSeal lets employers, registrars, and verifiers instantly authenticate diplomas, certificates, and degrees issued by partner institutions — using a single ID and voucher code.
              </motion.p>

              <div className="mt-10 flex flex-wrap items-center gap-8 text-sm text-slate-500 dark:text-slate-400">
                {initialLoading ? (
                  <SkeletonInlineStats count={3} />
                ) : (
                  <>
                    <div className="flex items-center gap-2" data-testid="stat-institutions">
                      <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span><strong className="text-slate-900 dark:text-white font-display font-extrabold text-base">{stats.total_institutions}</strong> {stats.total_institutions === 1 ? "institution" : "institutions"}</span>
                    </div>
                    <div className="flex items-center gap-2" data-testid="stat-graduates">
                      <GraduationCap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span><strong className="text-slate-900 dark:text-white font-display font-extrabold text-base">{stats.total_graduates}</strong> verified {stats.total_graduates === 1 ? "graduate" : "graduates"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <span>Tamper-proof vouchers</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-slate-800 rounded-2xl shadow-[0_8px_40px_rgba(15,23,42,0.06)] dark:shadow-[0_8px_40px_rgba(0,0,0,0.35)] p-8 md:p-10 focus-within:ring-2 focus-within:ring-blue-500/20 transition-shadow"
                data-testid="verify-card"
              >
                <div className="flex items-center gap-2 mb-1">
                  <FileCheck2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-700 dark:text-slate-300">Verify a credential</span>
                </div>
                <h2 className="font-display font-extrabold text-2xl text-slate-900 dark:text-white tracking-tight mt-1">Validate Certificate</h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400" data-testid="validate-instruction">
                  Enter the codes exactly as you have seen
                </p>

                <form onSubmit={handleVerify} className="mt-8 space-y-5">
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">Certificate ID</label>
                    <Input
                      placeholder="CS-XXXXXXXXXX"
                      value={certId}
                      onChange={(e) => setCertId(e.target.value.toUpperCase())}
                      className="h-12 text-base font-mono tracking-wider border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white focus-visible:ring-2 focus-visible:ring-blue-500"
                      data-testid="verify-cert-id-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">Voucher Code</label>
                    <Input
                      placeholder="XXXX-XXXX-XXXX"
                      value={voucher}
                      onChange={(e) => setVoucher(e.target.value.toUpperCase())}
                      className="h-12 text-base font-mono tracking-wider border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white focus-visible:ring-2 focus-visible:ring-blue-500"
                      data-testid="verify-voucher-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition-all font-display font-semibold text-base"
                    data-testid="verify-submit-btn"
                  >
                    {loading ? "Verifying…" : (<><ShieldCheck className="w-5 h-5 mr-2" /> Verify Credential</>)}
                  </Button>
                </form>

                <p className="mt-6 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Each voucher can be used up to 3 times — but only for the credential it was first paired with.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="relative py-20 md:py-28 bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400 mb-3">How it works</div>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight text-slate-900 dark:text-white">
              Built for institutions. Designed for verifiers.
            </h2>
            <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Three friction-free steps from credential issuance to public verification.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: KeyRound, num: "01", title: "Institutions issue", text: "Registered universities and academies create digital certificates and generate bulk voucher codes for their graduates." },
              { icon: Eye, num: "02", title: "Verifiers query", text: "Anyone with a Certificate ID and a paired voucher code can instantly retrieve the original credential." },
              { icon: Lock, num: "03", title: "Single-credential binding", text: "Each voucher binds to the first credential it validates — 3 uses total, never cross-usable. Forgery-proof by design." },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-8 hover:-translate-y-1 hover:shadow-lg dark:hover:shadow-black/40 hover:border-slate-300 dark:hover:border-slate-700 transition-all"
                data-testid={`how-card-${i}`}
              >
                <div className="flex items-baseline justify-between mb-6">
                  <div className="w-12 h-12 rounded-md bg-slate-900 dark:bg-blue-600 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-blue-400 dark:text-white" strokeWidth={2.5} />
                  </div>
                  <span className="font-display font-extrabold text-3xl text-slate-200 dark:text-slate-700">{item.num}</span>
                </div>
                <h3 className="font-display font-bold text-xl text-slate-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Institutions registry */}
      <section id="institutions" className="py-20 md:py-28">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400 mb-3">Partner registry</div>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-slate-900 dark:text-white">
                Registered institutions
              </h2>
              <p className="mt-3 text-base text-slate-600 dark:text-slate-400">
                {instSearch.trim()
                  ? `${totalMatches} ${totalMatches === 1 ? "match" : "matches"} for "${instSearch.trim()}"`
                  : `${stats.total_institutions} institutions issuing credentials to ${stats.total_graduates} verified graduates.`}
              </p>
            </div>
            {/* Search bar */}
            <div className="w-full md:w-80 relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                type="text"
                value={instSearch}
                onChange={(e) => setInstSearch(e.target.value)}
                placeholder="Search institutions by name…"
                className="h-11 pl-9 pr-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                data-testid="inst-search-input"
                aria-label="Search institutions by name"
              />
              {instSearch && (
                <button
                  type="button"
                  onClick={() => setInstSearch("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1"
                  aria-label="Clear search"
                  data-testid="inst-search-clear-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {initialLoading ? (
            <SkeletonCardGrid count={6} />
          ) : totalMatches === 0 ? (
            <Card className="p-12 text-center border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="no-institutions">
              <Building2 className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400">
                {instSearch.trim() ? `No institutions match "${instSearch.trim()}".` : "No institutions onboarded yet."}
              </p>
              {instSearch.trim() && (
                <Button variant="outline" size="sm" onClick={() => setInstSearch("")} className="mt-4" data-testid="inst-search-reset-btn">
                  Clear search
                </Button>
              )}
            </Card>
          ) : (
            <div className="space-y-12" data-testid="institutions-grid">
              {CATEGORY_ORDER.map((cat) => {
                const list = grouped[cat];
                if (!list || list.length === 0) return null;
                const searching = !!instSearch.trim();
                const isExpanded = searching || !!expanded[cat];
                const shown = isExpanded ? list : list.slice(0, 3);
                const remaining = list.length - shown.length;
                return (
                  <div key={cat} data-testid={`group-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="flex items-baseline justify-between mb-5 flex-wrap gap-2">
                      <h3 className="font-display font-bold text-xl md:text-2xl text-slate-900 dark:text-white flex items-baseline gap-3">
                        <span>{CATEGORY_LABEL[cat]}</span>
                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{list.length}</span>
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {shown.map((inst, i) => (
                        <motion.div
                          key={inst.id}
                          initial={{ opacity: 0, y: 12 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: i * 0.04 }}
                          onClick={() => navigate(`/institutions/${inst.slug}`)}
                          className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-7 hover:-translate-y-1 hover:shadow-lg dark:hover:shadow-black/40 hover:border-slate-300 dark:hover:border-slate-700 transition-all cursor-pointer"
                          data-testid={`inst-card-${inst.id}`}
                        >
                          <div className="flex items-start justify-between mb-5">
                            {inst.logo_path ? (
                              <img src={fileUrl(inst.logo_path)} alt="" className="w-11 h-11 object-contain rounded-md border border-slate-200 dark:border-slate-700 bg-white p-1" />
                            ) : (
                              <div className="w-11 h-11 rounded-md bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center border border-blue-100 dark:border-blue-900">
                                <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2.5} />
                              </div>
                            )}
                            <div className="text-right">
                              <div className="font-display font-extrabold text-2xl text-slate-900 dark:text-white leading-none">{inst.graduate_count}</div>
                              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 dark:text-slate-400 mt-1">Graduates</div>
                            </div>
                          </div>
                          <h4 className="font-display font-bold text-lg text-slate-900 dark:text-white leading-tight">{inst.name}</h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Est. {inst.year_established}</p>
                          <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-800 space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
                            <div className="truncate">{inst.address}</div>
                            <div className="truncate">{inst.email}</div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    {!searching && list.length > 3 && (
                      <div className="mt-6 flex justify-center">
                        <Button
                          variant="outline"
                          onClick={() => setExpanded((e) => ({ ...e, [cat]: !isExpanded }))}
                          className="rounded-full"
                          data-testid={`expand-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {isExpanded ? (<><ChevronUp className="w-4 h-4 mr-2" /> Show less</>) : (<><ChevronDown className="w-4 h-4 mr-2" /> Show {remaining} more</>)}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-slate-900 dark:bg-slate-950 text-white relative overflow-hidden border-t border-transparent dark:border-slate-800">
        <div className="absolute inset-0 bg-dotted opacity-10 pointer-events-none" />
        <div className="relative w-full max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-tight">Run an institution?</h2>
          <p className="mt-5 text-lg text-slate-300 max-w-2xl mx-auto">Onboard your registrar and issue tamper-proof, instantly verifiable diplomas at scale.</p>
          <div className="mt-10">
            <a href="mailto:admin@certiseal.net">
              <Button size="lg" className="h-12 px-8 bg-white text-slate-900 hover:bg-slate-100 font-display font-semibold" data-testid="cta-contact-btn">
                Contact our partnerships team <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <footer className="bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 py-10">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-2">
            <img src="/certiseal-logo.png" alt="CertiSeal" className="w-5 h-5 rounded-full dark:bg-white dark:p-0.5" />
            <span className="font-display font-bold text-slate-900 dark:text-white">CertiSeal</span>
            <span>· Your Trusted Academic Proof.</span>
          </div>
          <div>
            © {new Date().getFullYear()} CertiSeal. All rights{" "}
            <Link
              to="/login"
              className="underline decoration-dotted underline-offset-4 hover:text-slate-900 dark:hover:text-white transition-colors"
              data-testid="footer-reserved-link"
              title="Administrator access"
            >
              reserved
            </Link>.
          </div>
        </div>
      </footer>
    </div>
  );
}
